/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from 'react'
import { StyleSheet, css } from 'aphrodite'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  Tooltip,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Alert from '@material-ui/lab/Alert'
import CloseIcon from '@material-ui/icons/Close'
import WhatsAppIcon from '@material-ui/icons/WhatsApp'
import EditIcon from '@material-ui/icons/Edit'

import { Link } from 'react-router-dom'
import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { AppConfig } from '../../../AppConfig'
import { AppStyles, AppTheme } from '../../../styles/AppTheme'
import { AppHelper } from '../../../helpers/AppHelper'
import { CustomDynamicFormStyles } from '../../../styles/CustomStyles'
import { AppButton } from '../../../common/components/AppButton'
import { WhatsappTemplatePreview } from '../../../common/components/WhatsappTemplatePreview'
import { DocumentUploader } from '../../../common/components/DocumentUploader'
import { ImageUploaderWithAssetPicker } from '../../../components/ImageUploaderWithAssetPicker'
import { buildPreviewComponentsFromTemplate } from './WhatsappPreviewAdapter'
import {
  normalizeWaComponentsMap,
  buildWaComponentsFromBindings,
  getWaMappingSlots,
  areAllWaMappingSlotsFilled,
  remapWaComponentsToTemplate,
  waComponentsMapsEqual,
  getWaMappingHealth,
  shouldResetWaVariableBindings,
  getFieldOptions,
  getCrmNumbersRoute,
} from './WorkflowHelper'

const WA_MEDIA_ASSET_PICKER_Z = 140000
const WA_IMAGE_ALLOWED_FORMATS = ['png', 'jpeg', 'jpg']

const FormStyles = AppConfig.CUSTOM_STYLE ? CustomDynamicFormStyles : StyleSheet.create({
  formControl: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  autoCompleteInputRoot: {
    paddingRight: 56,
  },
  popupIndicator: {
    marginRight: 5,
  },
  clearIndicatorAlwaysVisible: {
    visibility: 'visible',
    opacity: 1,
    pointerEvents: 'auto',
  },
  clearIndicatorHidden: {
    display: 'none !important',
  },
  dialog: {
    zIndex: 130000,
    fontSize: 16,
    color: '#222222',
  },
  option: {
    fontSize: 16,
    color: '#222222',
  },
  labelContainer: {
    display: 'flex',
    marginBottom: 8,
  },
})

const DEFAULT_CONTACT_VARS = [
  { value: '{{contact.givenname}}', label: 'First Name' },
  { value: '{{contact.familyname}}', label: 'Last Name' },
  { value: '{{contact.name}}', label: 'Full Name' },
  { value: '{{contact.phone}}', label: 'Phone Number' },
  { value: '{{contact.email}}', label: 'Email' },
  { value: '{{contact.company}}', label: 'Company' },
]

export function isMissingDefaultWaNumberError(message) {
  return String(message || '').toLowerCase().includes('account default whatsapp number is not configured')
}

export function buildUnmappedTemplateKeyReason(key) {
  return `WhatsApp template key '${key}' is not mapped to a template`
}

export function collectMappedTemplateKeys(assignments) {
  const mapped = new Set()
  if (!assignments || typeof assignments !== 'object') return mapped
  Object.keys(assignments).forEach((templateName) => {
    const keys = assignments[templateName]
    ;(Array.isArray(keys) ? keys : []).forEach((k) => {
      const normalized = String(k || '').trim()
      if (normalized) mapped.add(normalized)
    })
  })
  return mapped
}

export function findPrimaryWaFromNumber(crmNumbersResult) {
  const items = (crmNumbersResult && Array.isArray(crmNumbersResult.items))
    ? crmNumbersResult.items
    : (Array.isArray(crmNumbersResult) ? crmNumbersResult : [])
  const primary = items.find((row) => row && row.primaryWaNumber === 'YES')
  return (primary && (primary.phone || primary.fromNumber || primary.key)) || ''
}

/** True when GET /crmnumbers?provider=WHATSAPP returned no onboarded numbers. */
export function isWhatsAppCrmNumbersEmpty(crmNumbersResult) {
  if (crmNumbersResult == null) return true
  const items = Array.isArray(crmNumbersResult.items)
    ? crmNumbersResult.items
    : (Array.isArray(crmNumbersResult) ? crmNumbersResult : null)
  if (items == null) return true
  return items.length === 0
}

export function getLocalTemplateKeyHealthError(key, { mappedWaKeys, defaultWaMissing } = {}) {
  const normalized = String(key || '').trim()
  if (!normalized) return null
  if (defaultWaMissing) {
    return 'Account default WhatsApp number is not configured'
  }
  if (mappedWaKeys && !mappedWaKeys.has(normalized)) {
    return buildUnmappedTemplateKeyReason(normalized)
  }
  return null
}

function buildTemplateKeyOptions(fieldDef, workflow) {
  const rawOptions = getFieldOptions(fieldDef || (workflow && workflow.fieldDef), 'waTemplateKey')
  const opts = []
  const seen = new Set()
  ;(Array.isArray(rawOptions) ? rawOptions : []).forEach((opt) => {
    const key = typeof opt === 'object' ? (opt.key || opt.value) : opt
    if (!key || key === 'NONE' || key === 'none') return
    const normalized = String(key)
    if (seen.has(normalized)) return
    seen.add(normalized)
    opts.push({
      key: normalized,
      label: typeof opt === 'object' ? (opt.label || opt.text || normalized) : normalized,
      text: typeof opt === 'object' ? (opt.text || opt.label || normalized) : normalized,
    })
  })
  const currentKey = workflow && workflow.waTemplateKey
  if (currentKey && !seen.has(currentKey)) {
    opts.unshift({ key: currentKey, label: currentKey, text: currentKey })
  }
  return opts
}

export function WorkflowWhatsAppDialog(props) {
  const {
    open,
    onClose,
    workflow,
    orgId,
    fieldDef,
    templateVariables: templateVariablesProp,
    onSaveSuccess,
    showToast,
  } = props

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('')
  const [waTemplate, setWaTemplate] = useState(null)
  const [waComponents, setWaComponents] = useState({})
  const [templateVariables, setTemplateVariables] = useState(DEFAULT_CONTACT_VARS)
  const [hydrateError, setHydrateError] = useState(null)
  const [prereqWarning, setPrereqWarning] = useState(null)
  const [isHydrating, setIsHydrating] = useState(false)
  const [mappingsDirty, setMappingsDirty] = useState(false)
  const [waCrmNumberMissing, setWaCrmNumberMissing] = useState(false)

  // Sub-dialog state for editing single variable
  const [editingVar, setEditingVar] = useState(null)
  const [varRadioValue, setVarRadioValue] = useState('')
  const [varCustomText, setVarCustomText] = useState('')
  const [varMediaLink, setVarMediaLink] = useState('')
  const [varMedia, setVarMedia] = useState('')
  const [varFileName, setVarFileName] = useState('')
  const uploadLocation = orgId ? `${orgId}/__FILENAME__` : ''
  const uploadMs = AppConfig.ENDPOINT

  const templateKeyOptions = useMemo(
    () => buildTemplateKeyOptions(fieldDef, workflow),
    [fieldDef, workflow]
  )

  const selectedTemplateOption = useMemo(() => {
    if (!selectedTemplateKey) return null
    return templateKeyOptions.find((o) => o.key === selectedTemplateKey)
      || { key: selectedTemplateKey, label: selectedTemplateKey, text: selectedTemplateKey }
  }, [selectedTemplateKey, templateKeyOptions])

  const mappingSlots = useMemo(
    () => getWaMappingSlots(waComponents, waTemplate),
    [waComponents, waTemplate]
  )

  const mappingHealth = useMemo(() => {
    if (!selectedTemplateKey || hydrateError) {
      return { broken: false, reason: null }
    }
    return getWaMappingHealth({
      waTemplateKey: selectedTemplateKey,
      waTemplate,
      waComponents,
    })
  }, [selectedTemplateKey, waTemplate, waComponents, hydrateError])

  const applyTemplateVariables = (vars) => {
    if (Array.isArray(vars) && vars.length > 0) {
      setTemplateVariables(
        vars.map((v) => ({
          value: v.value || v.key,
          label: v.text || v.label || v.value || v.key,
        }))
      )
      return
    }
    setTemplateVariables(DEFAULT_CONTACT_VARS)
  }

  // Always GET-by-id on open: BE returns the live Meta template for the current key
  // (it does not rehydrate stored waTemplate). Remap stored bindings onto that live shape.
  // Also probe WhatsApp CRM numbers in parallel — hydrate only reports missing default WA
  // after a waTemplateKey is set.
  useEffect(() => {
    if (!open || !workflow || !workflow.id) return

    const initialKey = (workflow.waTemplateKey || '').trim()
    setSelectedTemplateKey(initialKey)
    setWaTemplate(null)
    setWaComponents({})
    setMappingsDirty(false)
    setWaCrmNumberMissing(false)
    setHydrateError(
      workflow._waHydrateError
      || (workflow._waKeyBroken ? (workflow._waKeyBrokenReason || null) : null)
    )
    setPrereqWarning(workflow._prereqWarning || null)
    applyTemplateVariables(
      (Array.isArray(workflow.templateVariables) && workflow.templateVariables.length > 0)
        ? workflow.templateVariables
        : templateVariablesProp
    )
    setIsLoading(true)
    fetchWaCrmNumbers()
    fetchWaTemplateStructure()
  }, [open, workflow])

  const fetchWaCrmNumbers = async () => {
    if (!orgId) {
      setWaCrmNumberMissing(true)
      return
    }
    try {
      const resp = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId, provider: 'WHATSAPP' },
      })
      setWaCrmNumberMissing(isWhatsAppCrmNumbersEmpty(resp))
    } catch (err) {
      // Do not block the dialog on a transient numbers lookup failure
      setWaCrmNumberMissing(false)
    }
  }

  const fetchWaTemplateStructure = async () => {
    if (!workflow || !workflow.id) return
    setIsLoading(true)
    try {
      const resp = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['workflows', workflow.id],
        queryParams: { orgId, pb: 'fieldDef' },
      })
      const hydrateFailed = Boolean(
        (resp && resp._waHydrateError)
        || (resp && resp._prereqWarning)
        || (resp && resp._waKeyBroken)
      )

      if (resp && resp.waTemplateKey != null) {
        setSelectedTemplateKey((resp.waTemplateKey || '').trim())
      }

      if (hydrateFailed || !(resp && resp.waTemplate)) {
        setWaTemplate(null)
        setWaComponents({})
        setMappingsDirty(false)
      } else {
        // Always try to keep current mappings; remap drops only incompatible slots.
        // Warn (mappingsDirty / structure change) when variable count or type/format changes.
        setWaTemplate(resp.waTemplate)
        const listBindings = normalizeWaComponentsMap((workflow && workflow.waComponents) || {})
        const respBindings = normalizeWaComponentsMap(resp.waComponents || {})
        const storedTemplate = (workflow && workflow.waTemplate) || null
        const bindingSource = Object.keys(listBindings).length > 0 ? listBindings : respBindings
        const structureChanged = shouldResetWaVariableBindings(
          storedTemplate,
          resp.waTemplate,
          bindingSource
        )
        const skeleton = Object.keys(respBindings).length > 0 ? respBindings : bindingSource
        const remapped = remapWaComponentsToTemplate(resp.waTemplate, bindingSource, skeleton)
        setWaComponents(remapped)
        setMappingsDirty(structureChanged || !waComponentsMapsEqual(bindingSource, remapped))
      }
      if (Array.isArray(resp && resp.templateVariables) && resp.templateVariables.length > 0) {
        applyTemplateVariables(resp.templateVariables)
      }
      if (resp && resp._prereqWarning) {
        setPrereqWarning(resp._prereqWarning)
      } else {
        setPrereqWarning(null)
      }
      if (resp && resp._waHydrateError) {
        setHydrateError(resp._waHydrateError)
      } else if (
        hydrateFailed
        || (resp && !resp.waTemplate && !Object.keys(normalizeWaComponentsMap(resp.waComponents || {})).length
          && (resp.waTemplateKey || workflow.waTemplateKey))
      ) {
        setHydrateError(
          (resp && resp._waKeyBrokenReason)
          || 'WhatsApp template details could not be loaded for this key. '
          + 'Check that the template tag resolves to an approved Meta template on the account WhatsApp number.'
        )
      } else {
        setHydrateError(null)
      }
    } catch (err) {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to load WhatsApp template details'
      setWaTemplate(null)
      setWaComponents({})
      setHydrateError(errMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const applyWorkflowWaResponse = (updated) => {
    if (!updated) return
    const clearedKey = updated.waTemplateKey === ''
      || (Array.isArray(updated.REMOVE_ATTRS) && updated.REMOVE_ATTRS.includes('waTemplateKey'))
    if (clearedKey) {
      setSelectedTemplateKey('')
    } else if (updated.waTemplateKey != null) {
      setSelectedTemplateKey(updated.waTemplateKey || '')
    }

    const hydrateFailed = Boolean(updated._prereqWarning || updated._waHydrateError || updated._waKeyBroken)
    const removedPayload = Array.isArray(updated.REMOVE_ATTRS) && (
      updated.REMOVE_ATTRS.includes('waTemplate')
      || updated.REMOVE_ATTRS.includes('waComponents')
    )

    if (clearedKey || hydrateFailed || removedPayload) {
      setWaTemplate(null)
      setWaComponents({})
      setMappingsDirty(false)
    } else {
      if (updated.waTemplate !== undefined) {
        setWaTemplate(updated.waTemplate || null)
      }
      if (updated.waComponents !== undefined) {
        const comps = normalizeWaComponentsMap(updated.waComponents || {})
        if (updated.waTemplate) {
          const remapped = remapWaComponentsToTemplate(updated.waTemplate, comps, comps)
          setWaComponents(remapped)
          setMappingsDirty(false)
        } else {
          setWaComponents(comps)
          setMappingsDirty(false)
        }
      }
    }

    const nextHydrateError = updated._waHydrateError
      || (updated._waKeyBroken ? (updated._waKeyBrokenReason || buildUnmappedTemplateKeyReason(updated.waTemplateKey || '')) : null)
      || (
        !clearedKey
        && !updated.waTemplate
        && !Object.keys(normalizeWaComponentsMap(updated.waComponents || {})).length
        && updated.waTemplateKey
          ? (
            updated._waKeyBrokenReason
            || 'WhatsApp template details could not be loaded for this key. '
              + 'Check that the template tag resolves to an approved Meta template on the account WhatsApp number.'
          )
          : null
      )
    setHydrateError(nextHydrateError)
    setPrereqWarning(updated._prereqWarning || null)
    if (Array.isArray(updated.templateVariables) && updated.templateVariables.length > 0) {
      applyTemplateVariables(updated.templateVariables)
    }
  }

  // Preview-only hydrate (non-mutating). Does not update persisted workflow state.
  const applyPreviewResponse = (preview, selectedKey, previousBindings = {}) => {
    if (!preview) {
      setWaTemplate(null)
      setWaComponents({})
      setHydrateError('WhatsApp template details could not be loaded for this key.')
      return
    }

    const hydrateFailed = Boolean(preview._prereqWarning || preview._waHydrateError || preview._waKeyBroken)
    if (hydrateFailed || !preview.waTemplate) {
      setWaTemplate(null)
      setWaComponents({})
      setMappingsDirty(false)
    } else {
      setWaTemplate(preview.waTemplate || null)
      const skeleton = normalizeWaComponentsMap(preview.waComponents || {})
      const previous = normalizeWaComponentsMap(previousBindings)
      const remapped = remapWaComponentsToTemplate(
        preview.waTemplate,
        Object.keys(previous).length > 0 ? previous : skeleton,
        skeleton
      )
      setWaComponents(remapped)
      // New key preview: treat remapped skeleton as clean until user edits
      setMappingsDirty(false)
    }

    const nextHydrateError = preview._waHydrateError
      || (preview._waKeyBroken
        ? (preview._waKeyBrokenReason || buildUnmappedTemplateKeyReason(selectedKey || preview.waTemplateKey || ''))
        : null)
      || (
        !preview.waTemplate
        && !Object.keys(normalizeWaComponentsMap(preview.waComponents || {})).length
          ? (
            preview._waKeyBrokenReason
            || 'WhatsApp template details could not be loaded for this key. '
              + 'Check that the template tag resolves to an approved Meta template on the account WhatsApp number.'
          )
          : null
      )
    setHydrateError(nextHydrateError)
    setPrereqWarning(preview._prereqWarning || null)
    if (Array.isArray(preview.templateVariables) && preview.templateVariables.length > 0) {
      applyTemplateVariables(preview.templateVariables)
    }
  }

  const fetchWaTemplatePreview = async (key) => {
    return ApiHelper.callAwait({
      ms: AppConfig.CDP_MS.ENDPOINT,
      method: 'POST',
      paths: ['miscs', 'wa-template-preview'],
      queryParams: { orgId },
      jsonBody: { id: workflow.id, waTemplateKey: key },
    })
  }

  // Dropdown change: local selection + preview hydrate. Persist only on Save.
  const handleTemplateKeyChange = async (newKey) => {
    const key = (newKey || '').trim()
    if (!workflow || !workflow.id) return
    if (key === (selectedTemplateKey || '')) return

    // Clear is local until Save
    if (!key) {
      setSelectedTemplateKey('')
      setWaTemplate(null)
      setWaComponents({})
      setMappingsDirty(false)
      setHydrateError(null)
      setPrereqWarning(null)
      return
    }

    setSelectedTemplateKey(key)
    setMappingsDirty(false)
    setHydrateError(null)
    setPrereqWarning(null)

    const savedKey = (workflow.waTemplateKey || '').trim()
    if (key === savedKey) {
      setHydrateError(
        workflow._waHydrateError
        || (workflow._waKeyBroken ? (workflow._waKeyBrokenReason || null) : null)
      )
      setPrereqWarning(workflow._prereqWarning || null)
      // Re-fetch live template for the saved key (do not trust list/cached waTemplate)
      fetchWaTemplateStructure()
      return
    }

    // Preview a new (not yet persisted) key — do not carry incompatible media bindings
    setWaTemplate(null)
    setWaComponents({})
    setIsHydrating(true)
    try {
      const preview = await fetchWaTemplatePreview(key)
      applyPreviewResponse(preview, key, {})
    } catch (err) {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to preview WhatsApp template'
      setWaTemplate(null)
      setWaComponents({})
      setHydrateError(errMsg)
      if (showToast) showToast(errMsg, 'error')
    } finally {
      setIsHydrating(false)
    }
  }

  const openEditVariable = (slot) => {
    const bound = (waComponents && waComponents[slot.id]) || slot || {}
    const currentText = bound.text || ''
    const currentMediaLink = bound.mediaLink || ''
    const currentMedia = bound.media || ''
    const currentFileName = bound.fileName || ''

    const isMatchingContactVar = templateVariables.some((v) => v.value === currentText)
    if (isMatchingContactVar) {
      setVarRadioValue(currentText)
      setVarCustomText('')
    } else if (currentText) {
      setVarRadioValue('custom')
      setVarCustomText(currentText)
    } else {
      setVarRadioValue(templateVariables[0] ? templateVariables[0].value : 'custom')
      setVarCustomText('')
    }

    setVarMediaLink(currentMediaLink)
    setVarMedia(currentMedia)
    setVarFileName(currentFileName)
    setEditingVar(slot)
  }

  const handleMediaUploadSuccess = (_uploadId, signedUploadInfo) => {
    const link = (signedUploadInfo && signedUploadInfo.link) || ''
    const assetKey = (signedUploadInfo && signedUploadInfo.fields && signedUploadInfo.fields.key != null)
      ? String(signedUploadInfo.fields.key)
      : ''
    setVarMediaLink(link)
    setVarMedia(assetKey)
    if (editingVar && String(editingVar.format || '').toLowerCase() === 'document' && assetKey && !varFileName) {
      const base = assetKey.split('/').pop() || ''
      setVarFileName(base || 'Untitled')
    }
  }

  const saveVariableEdit = () => {
    if (!editingVar) return
    const slot = editingVar
    const currentBindings = normalizeWaComponentsMap(waComponents)
    const format = String(slot.format || '').toLowerCase()
    const isMedia = Boolean(slot.isMedia) || ['image', 'video', 'document'].includes(format)

    if (isMedia) {
      const hasMedia = Boolean(String(varMediaLink || varMedia || '').trim())
      if (!hasMedia) {
        if (showToast) showToast(`Provide ${format || 'media'} before continuing`, 'error')
        return
      }
      if (format === 'document' && !String(varFileName || '').trim()) {
        setVarFileName('Untitled')
      }
    }

    let textVal = ''
    if (isMedia) {
      textVal = varMediaLink || varMedia || ''
    } else if (varRadioValue === 'custom') {
      textVal = varCustomText
    } else {
      textVal = varRadioValue
    }

    const updated = {
      ...(currentBindings[slot.id] || {}),
      ...slot,
      id: String(slot.id),
      text: textVal,
      mediaLink: isMedia ? varMediaLink : '',
      media: isMedia ? (varMedia || varMediaLink) : '',
      fileName: isMedia && format === 'document'
        ? (String(varFileName || '').trim() || 'Untitled')
        : (varFileName || ''),
    }

    const nextBindings = {
      ...currentBindings,
      [String(slot.id)]: updated,
    }

    setWaComponents(buildWaComponentsFromBindings(mappingSlots, nextBindings))
    setMappingsDirty(true)
    setEditingVar(null)
  }

  // Save: persist key (set/clear) and/or dirty mappings. Preview never writes.
  const handleSave = async () => {
    if (!workflow || !workflow.id) return

    const savedKey = (workflow.waTemplateKey || '').trim()
    const pendingUnset = !selectedTemplateKey && Boolean(savedKey)
    const pendingKeyChange = Boolean(selectedTemplateKey) && selectedTemplateKey !== savedKey

    if (!pendingUnset && !pendingKeyChange && !mappingsDirty) {
      onClose()
      return
    }

    if (
      !pendingUnset
      && selectedTemplateKey
      && mappingSlots.length > 0
      && !areAllWaMappingSlotsFilled(mappingSlots, waComponents)
    ) {
      if (showToast) showToast('Map all template variables before saving', 'error')
      return
    }

    setIsSaving(true)
    try {
      let updated = null

      if (pendingUnset) {
        updated = await ApiHelper.callAwait({
          ms: AppConfig.CDP_MS.ENDPOINT,
          method: 'PUT',
          paths: ['workflows', workflow.id],
          queryParams: { orgId, pb: 'fieldDef' },
          jsonBody: { waTemplateKey: '' },
        })
        applyWorkflowWaResponse(updated || {
          waTemplateKey: '',
          waTemplate: null,
          waComponents: {},
        })
        if (showToast) showToast('WhatsApp template unset successfully', 'success')
        if (onSaveSuccess) {
          onSaveSuccess(updated || { ...workflow, waTemplateKey: '', waTemplate: null, waComponents: {} })
        }
        onClose()
        return
      }

      const shouldSaveMappings = Boolean(selectedTemplateKey && mappingsDirty)
      const mappingPayload = shouldSaveMappings
        ? {
          waComponents: buildWaComponentsFromBindings(
            mappingSlots,
            normalizeWaComponentsMap(waComponents)
          ),
        }
        : null

      if (pendingKeyChange) {
        updated = await ApiHelper.callAwait({
          ms: AppConfig.CDP_MS.ENDPOINT,
          method: 'PUT',
          paths: ['workflows', workflow.id],
          queryParams: { orgId, pb: 'fieldDef' },
          jsonBody: { waTemplateKey: selectedTemplateKey },
        })
        // If mappings follow, skip applying skeleton so local edits are not wiped
        if (!shouldSaveMappings) {
          applyWorkflowWaResponse(updated)
        }
      }

      if (shouldSaveMappings) {
        updated = await ApiHelper.callAwait({
          ms: AppConfig.CDP_MS.ENDPOINT,
          method: 'PUT',
          paths: ['workflows', workflow.id],
          queryParams: { orgId, pb: 'fieldDef' },
          jsonBody: mappingPayload,
        })
        applyWorkflowWaResponse(updated)
        if (showToast) {
          showToast(
            pendingKeyChange
              ? 'WhatsApp template key and mappings saved'
              : 'WhatsApp variable mappings saved',
            'success'
          )
        }
        if (onSaveSuccess) {
          onSaveSuccess(updated || {
            ...workflow,
            waTemplateKey: selectedTemplateKey,
            waComponents: mappingPayload.waComponents,
          })
        }
        onClose()
        return
      }

      if (pendingKeyChange) {
        if (showToast) showToast('WhatsApp template key updated', 'success')
        if (onSaveSuccess) {
          onSaveSuccess(updated || { ...workflow, waTemplateKey: selectedTemplateKey })
        }
        onClose()
      }
    } catch (err) {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to save WhatsApp configuration'
      if (showToast) showToast(errMsg, 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const savedKey = ((workflow && workflow.waTemplateKey) || '').trim()
  const pendingUnset = !selectedTemplateKey && Boolean(savedKey)
  const pendingKeyChange = Boolean(selectedTemplateKey) && selectedTemplateKey !== savedKey
  const allVariablesMapped = areAllWaMappingSlotsFilled(mappingSlots, waComponents)
  const hasUnmappedRequiredVars = Boolean(selectedTemplateKey)
    && !hydrateError
    && mappingSlots.length > 0
    && !allVariablesMapped
  const isSaveDisabled = isLoading || isHydrating
    || (!mappingsDirty && !pendingUnset && !pendingKeyChange)
    || hasUnmappedRequiredVars

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='md'
        fullWidth
        disableEnforceFocus
        style={{ zIndex: 13000 }}
        classes={{ paper: css(AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar) }}
        PaperProps={{
          style: {
            maxHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: AppTheme.borderRadius,
          },
        }}
      >
        <DialogTitle
          style={{
            flexShrink: 0,
            borderBottom: '1px solid #E5E7EB',
            padding: '16px 24px',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center' style={{ gap: 10 }}>
              <WhatsAppIcon style={{ color: '#25D366', fontSize: 26 }} />
              <Box>
                <Typography variant='h6' style={{ fontWeight: 700, color: '#111827', fontSize: 18 }}>
                  Configure WhatsApp — {workflow ? workflow.title : ''}
                </Typography>
                <Typography variant='body2' style={{ color: '#6B7280', fontSize: 12 }}>
                  Map a WhatsApp template to send automatically when this workflow is executed.
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size='small' style={{ color: '#6B7280' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            padding: '24px',
            backgroundColor: '#F9FAFB',
          }}
        >
          {/* Top Bar: WhatsApp Template Key Selection & Unset Action */}
          <Paper
            elevation={0}
            style={{
              padding: 16,
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              backgroundColor: '#FFFFFF',
              marginBottom: 20,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Box display='flex' flexDirection='column' style={{ gap: 12, width: '100%' }}>
              <FormControl fullWidth>
                <div className={css(FormStyles.labelContainer)}>
                  <Typography variant='body2' style={{ fontWeight: 600, color: '#222222' }}>
                    WhatsApp Template Key
                  </Typography>
                  <Tooltip title='Pick a WhatsApp template key to send on workflow execution'>
                    <i
                      className='material-icons-outlined'
                      style={{ fontSize: 14, color: '#7d7d7d', cursor: 'pointer', marginLeft: 5, alignSelf: 'center' }}
                    >
                      info
                    </i>
                  </Tooltip>
                </div>
                <Autocomplete
                  fullWidth
                  options={templateKeyOptions}
                  value={selectedTemplateOption}
                  disabled={isSaving || isHydrating}
                  onChange={(e, val) => {
                    const key = (val && val.key) || ''
                    handleTemplateKeyChange(key)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                    }
                  }}
                  getOptionLabel={(option) => (option && (option.label || option.text || option.key)) || ''}
                  getOptionSelected={(option, value) => Boolean(option && value && option.key === value.key)}
                  classes={{
                    popupIndicator: css(FormStyles.popupIndicator),
                    popper: css(FormStyles.dialog),
                    option: css(FormStyles.option),
                    clearIndicator: selectedTemplateOption
                      ? css(FormStyles.clearIndicatorAlwaysVisible)
                      : css(FormStyles.clearIndicatorHidden),
                  }}
                  renderOption={(option) => (
                    <span>{(option && (option.text || option.label || option.key)) || ''}</span>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='standard'
                      placeholder={
                        templateKeyOptions.length > 0
                          ? 'Select template key...'
                          : 'No WhatsApp template keys configured'
                      }
                      className={css(FormStyles.formControl)}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        classes: {
                          root: css([FormStyles.textInputStyles, FormStyles.autoCompleteInputRoot]),
                          input: css(FormStyles.formInputStyle),
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Paper>

          {/* Loading Indicator */}
          {(isLoading || isHydrating) && (
            <Box display='flex' alignItems='center' justifyContent='center' p={4} style={{ gap: 12 }}>
              <CircularProgress size={24} />
              <Typography variant='body2' color='textSecondary'>
                Loading WhatsApp template preview...
              </Typography>
            </Box>
          )}

          {/* Hydrate / prereq warnings */}
          {(waCrmNumberMissing || isMissingDefaultWaNumberError(hydrateError)) && !isHydrating && (
            <Box mb={2}>
              <Alert severity='warning'>
                <span>
                  No WhatsApp number is connected for this account.{' '}
                  <Link
                    to={getCrmNumbersRoute(orgId)}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Connect a WhatsApp number
                  </Link>
                </span>
              </Alert>
            </Box>
          )}
          {hydrateError && !isMissingDefaultWaNumberError(hydrateError) && !isLoading && !isHydrating && (
            <Box mb={2}>
              <Alert severity='warning'>
                {hydrateError}
              </Alert>
            </Box>
          )}
          {prereqWarning && !isLoading && !isHydrating && (
            <Box mb={2}>
              <Alert severity='info'>
                {typeof prereqWarning === 'string' ? prereqWarning : (prereqWarning.message || JSON.stringify(prereqWarning))}
              </Alert>
            </Box>
          )}

          {mappingHealth.broken && !hydrateError && !isLoading && !isHydrating && selectedTemplateKey && (
            <Box mb={2}>
              <Alert severity='warning'>
                {mappingHealth.reason
                  || 'WhatsApp template variables need to be remapped for the current template.'}
                {mappingHealth.formatMismatch
                  ? ' Variable count or type changed — review and remap any cleared fields before saving.'
                  : ''}
              </Alert>
            </Box>
          )}

          {/* When No Template Key is Selected */}
          {!selectedTemplateKey && !isLoading && !isHydrating && (
            <Box
              p={4}
              textAlign='center'
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: 8,
                border: '1px dashed #D1D5DB',
              }}
            >
              <WhatsAppIcon style={{ fontSize: 40, color: '#9CA3AF', marginBottom: 8 }} />
              <Typography variant='subtitle1' style={{ color: '#4B5563', fontWeight: 600 }}>
                No WhatsApp Template Configured
              </Typography>
              <Typography variant='body2' style={{ color: '#9CA3AF', marginTop: 4 }}>
                Select a &quot;WhatsApp Template Key&quot; above to send an automated WhatsApp message upon workflow execution.
              </Typography>
            </Box>
          )}

          {/* Key selected but template structure still missing after load */}
          {selectedTemplateKey && !isLoading && !isHydrating && !waTemplate && mappingSlots.length === 0 && (
            <Box
              p={4}
              textAlign='center'
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: 8,
                border: '1px dashed #D1D5DB',
              }}
            >
              <WhatsAppIcon style={{ fontSize: 40, color: '#9CA3AF', marginBottom: 8 }} />
              <Typography variant='subtitle1' style={{ color: '#4B5563', fontWeight: 600 }}>
                Template details unavailable
              </Typography>
              <Typography variant='body2' style={{ color: '#9CA3AF', marginTop: 4 }}>
                {hydrateError
                  ? 'Fix the WhatsApp template key mapping, or choose another key.'
                  : 'The key is set, but the WhatsApp template and variable mappings could not be loaded. Re-select the key or fix the template tag mapping, then try again.'}
              </Typography>
            </Box>
          )}

          {/* When Template is Selected & Loaded */}
          {selectedTemplateKey && !isLoading && !isHydrating && (waTemplate || mappingSlots.length > 0) && (
            <Grid container spacing={3}>
              {/* Left Column: Template Variables */}
              <Grid item xs={12} md={7}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FFFFFF',
                    height: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography variant='subtitle2' style={{ fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                    Template Variables ({mappingSlots.length})
                  </Typography>

                  {mappingSlots.length === 0 ? (
                    <Typography variant='body2' color='textSecondary'>
                      This WhatsApp template has no variable placeholders.
                    </Typography>
                  ) : (
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: 600, fontSize: 12 }}>Index</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: 12 }}>Format</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: 12 }}>Value</TableCell>
                          <TableCell align='right' style={{ fontWeight: 600, fontSize: 12 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappingSlots.map((slot, idx) => {
                          const bound = (waComponents && waComponents[slot.id]) || slot || {}
                          const format = String(slot.format || 'text').toLowerCase()
                          const mediaUrl = bound.mediaLink || ''
                          const displayVal = slot.isMedia
                            ? (mediaUrl || bound.media || bound.fileName || '[Media file]')
                            : (bound.text || '[Not set]')

                          return (
                            <TableRow key={slot.id}>
                              <TableCell style={{ fontSize: 12 }}>
                                {idx + 1}
                              </TableCell>
                              <TableCell style={{ fontSize: 12 }}>
                                {(slot.format || 'text').toUpperCase()}
                                {slot.vIndex ? ` {{${slot.vIndex}}}` : ''}
                              </TableCell>
                              <TableCell style={{ fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {slot.isMedia && format === 'image' && mediaUrl ? (
                                  <Tooltip title={displayVal} arrow placement='top'>
                                    <img
                                      src={mediaUrl}
                                      alt={bound.media || 'Media'}
                                      style={{ height: 36, maxWidth: 120, objectFit: 'contain', borderRadius: 4 }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={displayVal} arrow placement='top'>
                                    <span>{displayVal}</span>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell align='right'>
                                <Tooltip title='Edit Variable' placement='top'>
                                  <IconButton
                                    size='small'
                                    color='primary'
                                    onClick={() => openEditVariable(slot)}
                                  >
                                    <EditIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Grid>

              {/* Right Column: Live WhatsApp Preview */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FFFFFF',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='subtitle2' style={{ fontWeight: 700, color: '#1F2937', marginBottom: 12, width: '100%' }}>
                    WhatsApp Preview
                  </Typography>

                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {waTemplate && Array.isArray(waTemplate.components) ? (
                      <WhatsappTemplatePreview
                        components={buildPreviewComponentsFromTemplate(
                          waTemplate.components,
                          waComponents,
                          { hideTemplateExamplesWhenUnset: true }
                        )}
                      />
                    ) : (
                      <Typography variant='body2' color='textSecondary'>
                        Preview will appear after the template is resolved.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions
          style={{
            flexShrink: 0,
            borderTop: '1px solid #E5E7EB',
            padding: '16px 24px',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button onClick={onClose} style={{ textTransform: 'none', color: '#6B7280', fontWeight: 600 }}>
            Cancel
          </Button>

          <AppButton
            buttonStyle={isSaveDisabled ? FormStyles.buttonDisabled : FormStyles.button}
            buttonTextStyle={isSaveDisabled ? FormStyles.buttonDisabledText : FormStyles.buttonText}
            disabled={isSaveDisabled}
            inProcess={isSaving}
            onClick={handleSave}
            buttonText='Save Mappings'
          />
        </DialogActions>
      </Dialog>

      {/* Sub-Dialog for editing a variable */}
      {editingVar && (
        <Dialog
          open={Boolean(editingVar)}
          onClose={() => setEditingVar(null)}
          maxWidth='sm'
          fullWidth
          style={{ zIndex: 13002 }}
          disableEnforceFocus
          classes={{ paper: css(AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar) }}
          PaperProps={{
            style: {
              borderRadius: AppTheme.borderRadius,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle style={{ borderBottom: '1px solid #E5E7EB' }}>
            Edit {editingVar.label || editingVar.id}
          </DialogTitle>
          <DialogContent style={{ paddingTop: 20 }}>
            {editingVar.isMedia ? (
              <Box display='flex' flexDirection='column' style={{ gap: 16 }}>
                {String(editingVar.format || '').toLowerCase() === 'image' ? (
                  <Box>
                    <Typography variant='caption' style={{ fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                      Image (PNG/JPEG, max 1920px height)
                    </Typography>
                    <ImageUploaderWithAssetPicker
                      key={`wa-media-${editingVar.id}-${varMediaLink || 'empty'}`}
                      uploadId={`wa-media-${editingVar.id}`}
                      url={varMediaLink}
                      orgId={orgId}
                      uploadLocation={uploadLocation}
                      uploadMs={uploadMs}
                      uploadType='media'
                      uploadAccept='image/png, image/jpeg'
                      mode='fit'
                      height={1920}
                      borderRadius={0}
                      allowedFormats={WA_IMAGE_ALLOWED_FORMATS}
                      assetPickerZIndex={WA_MEDIA_ASSET_PICKER_Z}
                      onUploadSuccess={handleMediaUploadSuccess}
                    />
                  </Box>
                ) : String(editingVar.format || '').toLowerCase() === 'video' ? (
                  <Box>
                    <Typography variant='caption' style={{ fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                      Video (MP4, max 16MB)
                    </Typography>
                    <DocumentUploader
                      key={`wa-media-${editingVar.id}-${varMedia || varMediaLink || 'empty'}`}
                      label='Video'
                      uploadId={`wa-media-${editingVar.id}`}
                      link={varMediaLink}
                      value={varMedia || varMediaLink}
                      uploadLocation={uploadLocation}
                      uploadMs={uploadMs}
                      uploadType='media'
                      uploadAccept='video/mp4'
                      fileSizeLimit={16000000}
                      onUploadSuccess={handleMediaUploadSuccess}
                    />
                  </Box>
                ) : (
                  <Box>
                    <Typography variant='caption' style={{ fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                      Document (PDF, max 20MB)
                    </Typography>
                    <DocumentUploader
                      key={`wa-media-${editingVar.id}-${varMedia || varMediaLink || 'empty'}`}
                      label='Document'
                      uploadId={`wa-media-${editingVar.id}`}
                      link={varMediaLink}
                      value={varMedia || varMediaLink}
                      uploadLocation={uploadLocation}
                      uploadMs={uploadMs}
                      uploadType='media'
                      uploadAccept='application/pdf'
                      fileSizeLimit={20000000}
                      onUploadSuccess={handleMediaUploadSuccess}
                    />
                  </Box>
                )}
                {String(editingVar.format || '').toLowerCase() === 'document' && (
                  <TextField
                    label='File Name'
                    placeholder='e.g. Agreement.pdf'
                    variant='outlined'
                    size='small'
                    fullWidth
                    value={varFileName}
                    onChange={(e) => setVarFileName(e.target.value)}
                    helperText='Name displayed in WhatsApp chat for the document'
                  />
                )}
              </Box>
            ) : (
              <FormControl component='fieldset' fullWidth>
                <FormLabel component='legend' style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Contact Variable or Custom Text
                </FormLabel>
                <RadioGroup
                  value={varRadioValue}
                  onChange={(e) => setVarRadioValue(e.target.value)}
                >
                  {templateVariables.map((v) => (
                    <FormControlLabel
                      key={v.value}
                      value={v.value}
                      control={<Radio color='primary' size='small' />}
                      label={v.label}
                    />
                  ))}
                  <FormControlLabel
                    value='custom'
                    control={<Radio color='primary' size='small' />}
                    label='[Custom Text]'
                  />
                </RadioGroup>

                {varRadioValue === 'custom' && (
                  <Box mt={1}>
                    <TextField
                      label='Custom Value'
                      placeholder='Enter custom text or {{contact.attribute}}'
                      variant='outlined'
                      size='small'
                      fullWidth
                      value={varCustomText}
                      onChange={(e) => setVarCustomText(e.target.value)}
                    />
                  </Box>
                )}
              </FormControl>
            )}
          </DialogContent>
          <DialogActions style={{ borderTop: '1px solid #E5E7EB', padding: 16 }}>
            <Button onClick={() => setEditingVar(null)} style={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={saveVariableEdit}
              style={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#2563EB' }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
